import { RuleId, staticAltTextRule } from '@wix/a11y-rules';
import { Box } from '@wix/design-system';
import { useErrorHandler, useTranslation } from '@wix/fe-essentials/react';
import { convertElementStatusToBooleanStatus, ElementStatus } from '@wix/rules-panel-ui';
import { assertNotOptional } from '@wix/seo-common-core';
import { produce } from 'immer';
import { useAtomValue, useSetAtom } from 'jotai';
import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { altTextScreenPageIdAtom } from '../../../../../atoms/session-atoms';
import { BiCtaNames, BiModalTypes } from '../../../../../bi/types';
import { usePanelStateAtom } from '../../../../../hooks/rules-state';
import { useA11yWizardAdapter } from '../../../../../hooks/use-a11y-wizard-adapter';
import { useBiLogger } from '../../../../../hooks/use-bi-logger';
import { useSuggestionsData } from '../../../../../hooks/use-suggestions-data';
import { PanoramaErrorNames } from '../../../../../panorama/constants';
import { usePanoramaFunctions } from '../../../../../panorama/use-panorama-functions';
import { getUpdatedAltTextImagesFromState } from '../../../../../rule-mappers/rule-handlers/alt-text/get-updated-alt-text-images-from-state';
import { useGlobalSectionState } from '../../../../../rule-mappers/shared/use-global-section-state';
import { useRuleBase } from '../../../../../rule-mappers/shared/use-rule-base';
import { resizeImageUrl } from '../../../../../utils/resize-image-url';
import { showGenerateAllErrorModal } from '../../../../modals/show-generate-all-error-modal';
import { showSaveBeforeCloseModal } from '../../../../modals/show-save-before-leave-modal';
import { showSaveErrorModal } from '../../../../modals/show-save-error-modal';
import { showStopGeneratingSuggestionsModal } from '../../../../modals/show-stop-generating-suggestions-modal';
import { convertScanStatusToUiStatus } from '../../utils';
import { Pagination } from '../pagination-component/pagination';
const PAGE_SIZE = 50;
export const useAltTextScreen = props => {
  var _ruleState$state2, _ruleState$state4;
  const {
    getResolvedError
  } = useErrorHandler();
  const {
    altRulesProps,
    altHandlers,
    setAltRulesProps,
    staticAltHandlers
  } = props;
  const {
    setBulkAltText
  } = altHandlers;
  const {
    getRuleState,
    panelState,
    getPagesData
  } = usePanelStateAtom();
  const pagesData = getPagesData();
  const {
    a11yWizardAdapter,
    editorHost
  } = useA11yWizardAdapter();
  const {
    t
  } = useTranslation();
  const biLogger = useBiLogger();
  const {
    reportErrorPanorama
  } = usePanoramaFunctions();
  const {
    getMultipleSuggestionsData
  } = useSuggestionsData();
  const altTextScreenProps = useAtomValue(altTextScreenPageIdAtom);
  const {
    itemType,
    pageId
  } = altTextScreenProps || {};
  const {
    updateRuleStateAndVerifyIfNeeded,
    setIsApplyTriggered
  } = useRuleBase();
  const {
    updateGlobalSectionStateIfNeeded,
    getGlobalSectionModifiedComponentIds
  } = useGlobalSectionState();
  const [isGeneratingAltTextSuggestions, setIsGeneratingAltTextSuggestions] = useState(false);
  const {
    checker: checkStaticAltText
  } = staticAltTextRule;
  const containerRef = useRef(null);
  const [showPopoverAiWarning, setShowPopoverAiWarning] = useState(false);
  const [pageNumber, setPageNumber] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [shouldShowSummary, setShouldShowSummary] = useState(false);
  const [isSortedByUnsupported, setIsSortedByUnsupported] = useState(false);
  const setAltTextScreenPageId = useSetAtom(altTextScreenPageIdAtom);
  assertNotOptional(pageId);
  const ruleState = getRuleState(pageId, RuleId.STATIC_ALT_TEXT);
  const altRulePropsWithState = useMemo(() => {
    var _ruleState$state;
    return getUpdatedAltTextImagesFromState(altRulesProps, ruleState == null || (_ruleState$state = ruleState.state) == null ? void 0 : _ruleState$state.savedImages, panelState.globalSectionComponentsDataMap);
  }, [altRulesProps, panelState.globalSectionComponentsDataMap, ruleState == null || (_ruleState$state2 = ruleState.state) == null ? void 0 : _ruleState$state2.savedImages]);
  const pages = useMemo(() => _.chunk(altRulePropsWithState, PAGE_SIZE), [altRulePropsWithState]);
  const pagesAmount = pages.length;
  const isPagination = pagesAmount !== 1;
  const [altTextDraftDataMap, setAltTextDraftDataMap] = useState(new Map());
  const unreviewedAiImageIds = useRef(new Set());
  const navigateToPanel = useCallback(() => {
    setAltTextScreenPageId(undefined);
  }, [setAltTextScreenPageId]);
  const changedAltData = useMemo(() => {
    const altDataToSet = [];
    altRulesProps.forEach(rule => {
      const mapData = altTextDraftDataMap.get(rule.id);
      if (mapData !== undefined && ((mapData == null ? void 0 : mapData.isDecorative) !== rule.isDecorative || (mapData == null ? void 0 : mapData.alt) !== rule.alt)) {
        altDataToSet.push(mapData);
      }
    });
    return altDataToSet;
  }, [altRulesProps, altTextDraftDataMap]);
  const shouldShowModalSaveBeforeClose = changedAltData.length !== 0;
  const filledAmount = altRulesProps.filter(image => {
    const imageData = altTextDraftDataMap.get(image.id);
    if (imageData === undefined) {
      return convertElementStatusToBooleanStatus(image.status);
    } else {
      const checkResult = checkStaticAltText({
        images: [{
          ...imageData,
          uri: ''
        }]
      });
      return imageData.isDecorative || checkResult.failedElements.length === 0;
    }
  }).length;
  const failedElementCount = altRulesProps.length - filledAmount;
  const updateUnreviewedAiImageIds = useCallback(id => {
    var _altTextDraftDataMap$;
    const isGenerated = (altTextDraftDataMap == null || altTextDraftDataMap.get == null || (_altTextDraftDataMap$ = altTextDraftDataMap.get(id)) == null ? void 0 : _altTextDraftDataMap$.status) === ElementStatus.GeneratedAll;
    const currentPageIds = new Set(pages[pageNumber].map(rule => rule.id));
    const isInUnreviewedAiImageIds = unreviewedAiImageIds.current.has(id);
    if (isGenerated && !currentPageIds.has(id)) {
      unreviewedAiImageIds.current.add(id);
    } else if (!isGenerated && isInUnreviewedAiImageIds) {
      const updatedUnreviewedAiImageIds = new Set(unreviewedAiImageIds.current);
      updatedUnreviewedAiImageIds.delete(id);
      unreviewedAiImageIds.current = updatedUnreviewedAiImageIds;
    }
  }, [altTextDraftDataMap, pageNumber, pages]);
  const handleGenerateWithAiClick = useCallback(async elementsWithMissingAlt => {
    elementsWithMissingAlt.forEach(el => {
      const existingObj = altTextDraftDataMap.get(el.id);
      const updatedObj = {
        ...existingObj,
        alt: (existingObj == null ? void 0 : existingObj.alt) ?? el.alt ?? '',
        id: el.id,
        status: ElementStatus.Loading,
        tpaData: el.tpaData
      };
      altTextDraftDataMap.set(el.id, updatedObj);
    });
    setAltTextDraftDataMap == null || setAltTextDraftDataMap(new Map(altTextDraftDataMap));
    setIsGeneratingAltTextSuggestions(true);
    const imageSuggestionDataRequests = elementsWithMissingAlt.map(async _ref => {
      let {
        id,
        url,
        entityId
      } = _ref;
      return {
        entityId: entityId ?? pageId ?? '',
        id,
        url: await resizeImageUrl(url)
      };
    });
    const resolvedImageSuggestionData = await Promise.all(imageSuggestionDataRequests);
    const multipleSuggestionsData = await getMultipleSuggestionsData(itemType ?? '', resolvedImageSuggestionData);
    elementsWithMissingAlt.forEach(el => {
      var _foundSuggestion$item, _foundSuggestion$sugg;
      const foundSuggestion = multipleSuggestionsData.find(s => s.id === el.id);
      const success = foundSuggestion == null || (_foundSuggestion$item = foundSuggestion.itemMetadata) == null ? void 0 : _foundSuggestion$item.success;
      const suggestion = foundSuggestion == null || (_foundSuggestion$sugg = foundSuggestion.suggestions) == null ? void 0 : _foundSuggestion$sugg[0];
      if (!success || !suggestion) {
        altTextDraftDataMap == null || altTextDraftDataMap.set(el.id, {
          ...(altTextDraftDataMap == null ? void 0 : altTextDraftDataMap.get(el.id)),
          alt: el.alt ?? '',
          entityId: el.entityId,
          id: el.id,
          status: ElementStatus.Warning,
          tpaData: el.tpaData
        });
      } else {
        altTextDraftDataMap == null || altTextDraftDataMap.set(el.id, {
          ...(altTextDraftDataMap == null ? void 0 : altTextDraftDataMap.get(el.id)),
          alt: suggestion,
          entityId: el.entityId,
          id: el.id,
          status: ElementStatus.GeneratedAll,
          tpaData: el.tpaData
        });
        updateUnreviewedAiImageIds(el.id);
      }
    });
    const atLeastOneSucceeded = elementsWithMissingAlt.some(_ref2 => {
      var _altTextDraftDataMap$2;
      let {
        id
      } = _ref2;
      return (altTextDraftDataMap == null || altTextDraftDataMap.get == null || (_altTextDraftDataMap$2 = altTextDraftDataMap.get(id)) == null ? void 0 : _altTextDraftDataMap$2.status) === ElementStatus.GeneratedAll;
    });
    if (!atLeastOneSucceeded) {
      reportErrorPanorama(new Error('Failed to generate alt text suggestions'), PanoramaErrorNames.altTextOnGenerateMultipleWithAi);
      showGenerateAllErrorModal({
        a11yWizardAdapter,
        biLogger,
        editorHost,
        handleCancel: () => {
          biLogger.modalCtaClick({
            ctaName: BiCtaNames.Cancel,
            modalType: BiModalTypes.GenerateWithAiError
          });
        },
        handleTryAgain: () => {
          handleGenerateWithAiClick(elementsWithMissingAlt);
          biLogger.modalCtaClick({
            ctaName: BiCtaNames.TryAgain,
            modalType: BiModalTypes.GenerateWithAiError
          });
        },
        t
      });
    }
    setAltTextDraftDataMap == null || setAltTextDraftDataMap(new Map(altTextDraftDataMap));
    setIsGeneratingAltTextSuggestions(false);
  }, [a11yWizardAdapter, altTextDraftDataMap, biLogger, editorHost, getMultipleSuggestionsData, itemType, pageId, reportErrorPanorama, t, updateUnreviewedAiImageIds]);
  const handleClickApply = useCallback(async callback => {
    var _ruleState$state3;
    const stateMap = (ruleState == null || (_ruleState$state3 = ruleState.state) == null ? void 0 : _ruleState$state3.savedImages) ?? new Map();
    biLogger.taskElementApplyClick({
      elementIndexList: changedAltData.map(rule => altRulesProps.findIndex(ruleProp => ruleProp.id === rule.id)),
      elementStatusList: changedAltData.map(altData => altData.status),
      elementValueList: changedAltData.map(rule => rule.alt)
    });
    const staticChangedAltData = changedAltData.filter(altData => pagesData.get(altData.entityId ?? '') !== undefined);
    const verticalChangedAltData = changedAltData.filter(altData => pagesData.get(altData.entityId ?? '') === undefined);
    const updatedStateMap = produce(stateMap, draft => {
      changedAltData.forEach(altData => {
        draft.set(altData.id, altData);
      });
    });
    const updatedGlobalSectionDataMap = staticChangedAltData.length > 0 ? updateGlobalSectionStateIfNeeded(new Map(changedAltData.map(altData => {
      var _altData$tpaData;
      return [altData.id, {
        alt: altData.alt,
        componentId: ((_altData$tpaData = altData.tpaData) == null ? void 0 : _altData$tpaData.componentId) ?? altData.id,
        isDecorative: altData.isDecorative
      }];
    }))) : panelState.globalSectionComponentsDataMap;
    const altTextAfterApply = getUpdatedAltTextImagesFromState(altRulePropsWithState, updatedStateMap, updatedGlobalSectionDataMap);
    const altToChecker = altTextAfterApply.map(altData => {
      return {
        ...altData,
        alt: altData.alt ?? '',
        uri: altData.url
      };
    });
    const {
      status: statusAfterApply
    } = staticAltTextRule.checker({
      images: altToChecker.map(image => ({
        ...image,
        decorative: image.isDecorative
      }))
    });
    if (!showPopoverAiWarning && unreviewedAiImageIds.current.size > 0) {
      setShowPopoverAiWarning(true);
      biLogger.modalView({
        modalType: BiModalTypes.ReviewRecentChanges
      });
      return;
    } else if (showPopoverAiWarning) {
      setShowPopoverAiWarning(false);
    }
    setIsLoading(true);
    try {
      await Promise.all([setBulkAltText(verticalChangedAltData), staticAltHandlers == null || staticAltHandlers.setBulkAltText == null ? void 0 : staticAltHandlers.setBulkAltText(staticChangedAltData)]);
      updateRuleStateAndVerifyIfNeeded(pageId, RuleId.STATIC_ALT_TEXT, {
        savedImages: updatedStateMap,
        status: convertScanStatusToUiStatus(statusAfterApply)
      }, getGlobalSectionModifiedComponentIds(updatedGlobalSectionDataMap));
      if (failedElementCount === 0) {
        navigateToPanel();
      }
      setShouldShowSummary(false);
      setAltTextDraftDataMap == null || setAltTextDraftDataMap(new Map());
      setAltRulesProps(altTextAfterApply);
      callback == null || callback();
    } catch (error) {
      var _getResolvedError;
      reportErrorPanorama(error, PanoramaErrorNames.updateAltText);
      await showSaveErrorModal({
        a11yWizardAdapter,
        biLogger,
        editorHost,
        errorMessage: (_getResolvedError = getResolvedError(error)) == null ? void 0 : _getResolvedError.message,
        handleTryAgain: () => {
          handleClickApply(callback);
          updateRuleStateAndVerifyIfNeeded(pageId, RuleId.STATIC_ALT_TEXT, {
            savedImages: stateMap
          }, getGlobalSectionModifiedComponentIds(updatedGlobalSectionDataMap));
        },
        t
      });
      return;
    } finally {
      setIsLoading(false);
      setIsApplyTriggered(true);
    }
  }, [ruleState == null || (_ruleState$state4 = ruleState.state) == null ? void 0 : _ruleState$state4.savedImages, biLogger, changedAltData, updateGlobalSectionStateIfNeeded, panelState.globalSectionComponentsDataMap, altRulePropsWithState, showPopoverAiWarning, altRulesProps, pagesData, setBulkAltText, staticAltHandlers, updateRuleStateAndVerifyIfNeeded, pageId, getGlobalSectionModifiedComponentIds, failedElementCount, setAltRulesProps, navigateToPanel, reportErrorPanorama, a11yWizardAdapter, editorHost, getResolvedError, t, setIsApplyTriggered]);
  const scrollToTop = useCallback(() => {
    if (containerRef.current) {
      containerRef.current.scrollTo({
        top: 0
      });
    }
  }, [containerRef]);
  const onClickNext = useCallback(isTopPagination => {
    if (pageNumber < pagesAmount) {
      setPageNumber(pageNumber + 1);
      scrollToTop();
    }
    biLogger.taskCtaClick({
      ctaName: isTopPagination ? BiCtaNames.TopPaginationNext : BiCtaNames.BottomPaginationNext,
      elementCount: altRulesProps.length,
      failedElementCount
    });
  }, [altRulesProps.length, biLogger, failedElementCount, pageNumber, pagesAmount, scrollToTop]);
  const onClickBack = useCallback(isTopPagination => {
    if (pageNumber > 0) {
      setPageNumber(pageNumber - 1);
      scrollToTop();
    }
    biLogger.taskCtaClick({
      ctaName: isTopPagination ? BiCtaNames.TopPaginationPrev : BiCtaNames.BottomPaginationPrev,
      elementCount: altRulesProps.length,
      failedElementCount
    });
  }, [altRulesProps.length, biLogger, failedElementCount, pageNumber, scrollToTop]);
  useEffect(() => {
    const currentPageIds = new Set(pages[pageNumber].map(rule => rule.id));
    unreviewedAiImageIds.current = new Set([...unreviewedAiImageIds.current].filter(id => !currentPageIds.has(id)));
  }, [pageNumber, pages]);
  const getPaginationComponent = useCallback(_ref3 => {
    let {
      isTopPagination
    } = _ref3;
    return isPagination ? /*#__PURE__*/React.createElement(Box, {
      direction: "vertical",
      gap: "SP2",
      padding: isTopPagination ? 'SP3 SP4 SP2 SP4' : '0 SP4 SP1 0',
      width: "252px"
    }, /*#__PURE__*/React.createElement(Pagination, {
      currentPageNumber: pageNumber,
      pagesAmount: pagesAmount,
      pageSize: PAGE_SIZE,
      totalItems: altRulesProps.length,
      onClickBack: () => onClickBack(isTopPagination),
      onClickNext: () => onClickNext(isTopPagination)
    })) : null;
  }, [altRulesProps.length, isPagination, onClickBack, onClickNext, pageNumber, pagesAmount]);
  const showBeforeCloseModal = useCallback((afterApplyFunc, isGenerating) => {
    const shouldShowSaveBeforeCloseModal = changedAltData.length > 0;
    if (isGenerating) {
      showStopGeneratingSuggestionsModal({
        a11yWizardAdapter,
        biLogger,
        editorHost,
        handleApply: () => {},
        handleDiscard: () => {
          setIsGeneratingAltTextSuggestions(false);
          showBeforeCloseModal(afterApplyFunc, false);
        },
        t
      });
    } else if (shouldShowSaveBeforeCloseModal) {
      showSaveBeforeCloseModal({
        a11yWizardAdapter,
        biLogger,
        editorHost,
        handleApply: async () => {
          await handleClickApply(afterApplyFunc);
        },
        handleDiscard: afterApplyFunc,
        t
      });
    }
  }, [a11yWizardAdapter, biLogger, changedAltData.length, editorHost, handleClickApply, t]);
  return useMemo(() => ({
    actions: {
      altHandlers,
      getPaginationComponent,
      handleClickApply,
      handleGenerateWithAiClick,
      navigateToPanel,
      setAltRulesProps: props.setAltRulesProps,
      setAltTextDraftDataMap,
      setIsGeneratingAltTextSuggestions,
      setIsLoading,
      setIsSortedByUnsupported,
      setPageNumber,
      setShouldShowSummary,
      setShowPopoverAiWarning,
      showBeforeCloseModal,
      staticAltHandlers
    },
    state: {
      altRulesProps: altRulePropsWithState,
      altTextDraftDataMap,
      containerRef,
      failedElementCount,
      filledAmount,
      isGeneratingAltTextSuggestions,
      isLoading,
      isPagination,
      isSortedByUnsupported,
      itemType,
      pageId: props.pageId,
      pageName: props.pageName,
      pageNumber,
      pages,
      pagesAmount,
      shouldShowModalSaveBeforeClose,
      shouldShowSummary,
      showPopoverAiWarning,
      unreviewedAiImageIds
    }
  }), [altHandlers, getPaginationComponent, handleClickApply, handleGenerateWithAiClick, navigateToPanel, props.setAltRulesProps, props.pageId, props.pageName, showBeforeCloseModal, staticAltHandlers, altRulePropsWithState, altTextDraftDataMap, failedElementCount, filledAmount, isGeneratingAltTextSuggestions, isLoading, isPagination, isSortedByUnsupported, pageNumber, pages, pagesAmount, shouldShowModalSaveBeforeClose, shouldShowSummary, showPopoverAiWarning, itemType]);
};
//# sourceMappingURL=use-alt-text-screen.js.map